import { useCallback, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Columns3, Lock, RotateCcw } from 'lucide-react';
import type { ListMeta } from '../../types/api';
import type { CsvExport } from './ExportCsvButton';
import { ListToolbar } from './ListToolbar';
import { useAuth } from '../../stores/auth';
import { useAnchoredPanel } from './menu';
import { TableColumnsContext } from './tableColumns';
import { useHoldWhileLoading } from '../../lib/appReady';

export interface Column<T> {
  key: string;
  label: string;
  render?: (row: T) => ReactNode;
  align?: 'left' | 'right';
  width?: string;
  hideOnMobile?: boolean;
  /** false = this heading does not sort (default: sortable). */
  sortable?: boolean;
  /** The value to sort by when the cell is rendered from something other than row[key]. */
  sortValue?: (row: T) => unknown;
  /** The server's sort field for this column (with `onSort`); defaults to `key`. Omit to sort this page only. */
  sortKey?: string;
  /** With `columnPicker`: always shown, cannot be unticked (the name column, the actions column). */
  lockedVisible?: boolean;
  /** With `columnPicker`: hidden until the user ticks it. */
  defaultHidden?: boolean;
}

/** Saved choices: column key → shown, only for columns the user changed from the default. */
type ColumnChoices = Record<string, boolean>;

function readChoices(storageKey: string): ColumnChoices {
  try {
    const raw = window.localStorage.getItem(storageKey);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.fromEntries(Object.entries(parsed as Record<string, unknown>).filter(([, v]) => typeof v === 'boolean')) as ColumnChoices;
  } catch { return {}; }
}

function writeChoices(storageKey: string, choices: ColumnChoices) {
  try {
    if (Object.keys(choices).length) window.localStorage.setItem(storageKey, JSON.stringify(choices));
    else window.localStorage.removeItem(storageKey);
  } catch { /* storage unavailable: the choice lasts until the page is left */ }
}

/** Which columns the user sees, remembered per list in localStorage (falls back to the defaults). */
function useColumnVisibility<T>(columns: Column<T>[], storageKey: string | undefined) {
  const [choices, setChoices] = useState<ColumnChoices>(() => (storageKey ? readChoices(storageKey) : {}));
  const isShown = useCallback((c: Column<T>) => {
    if (!storageKey || c.lockedVisible || !c.label) return true;
    return choices[c.key] ?? !c.defaultHidden;
  }, [choices, storageKey]);
  const update = (next: ColumnChoices) => { setChoices(next); if (storageKey) writeChoices(storageKey, next); };
  const toggle = (c: Column<T>) => {
    const shown = !isShown(c);
    const next = { ...choices };
    if (shown === !c.defaultHidden) delete next[c.key]; else next[c.key] = shown;
    update(next);
  };
  const reset = () => update({});
  /** Show exactly these keys (of the columns that can be hidden); the rest hide. */
  const applyShown = (keys: string[]) => {
    const next: ColumnChoices = {};
    for (const c of columns) {
      if (c.lockedVisible || !c.label) continue;
      const shown = keys.includes(c.key);
      if (shown !== !c.defaultHidden) next[c.key] = shown;
    }
    update(next);
  };
  const visible = useMemo(() => columns.filter(isShown), [columns, isShown]);
  return { visible, isShown, toggle, reset, applyShown, changed: Object.keys(choices).some((k) => columns.some((c) => c.key === k)) };
}

/** The toolbar's "Columns" button: tick the columns to show. */
function ColumnPicker<T>({ columns, isShown, toggle, reset, changed }: { columns: Column<T>[]; isShown: (c: Column<T>) => boolean; toggle: (c: Column<T>) => void; reset: () => void; changed: boolean }) {
  const [open, setOpen] = useState(false);
  const { wrap, panel, pos } = useAnchoredPanel(open, setOpen, 'right');
  const pickable = columns.filter((c) => !!c.label);
  return (
    <div ref={wrap} className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-haspopup="dialog" aria-expanded={open} title="Choose the columns to show"
        className={`inline-flex h-7 items-center gap-1.5 rounded-lg border px-2.5 text-[12px] font-medium transition ${open ? 'border-bronze-500 bg-bronze-50 text-bronze-600' : 'border-ink-200 bg-white text-ink-600 hover:bg-ink-50 hover:text-ink-900'}`}>
        <Columns3 className="h-3.5 w-3.5" />Columns
      </button>
      {open && createPortal(
        <div ref={panel} role="dialog" aria-label="Columns" style={{ position: 'fixed', top: pos?.top ?? -9999, left: pos?.left ?? -9999 }} className="menu-pop z-50 w-56 overflow-hidden rounded-lg border border-ink-200 bg-white py-1 shadow-float">
          <p className="px-3 pb-1 pt-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-ink-500">Columns</p>
          <div className="max-h-[60vh] overflow-y-auto">
            {pickable.map((c) => (
              <label key={c.key} className={`flex items-center gap-2 px-3 py-1.5 text-[12.5px] ${c.lockedVisible ? 'cursor-not-allowed text-ink-400' : 'cursor-pointer text-ink-800 hover:bg-ink-50'}`} title={c.lockedVisible ? 'Always shown' : undefined}>
                <input type="checkbox" checked={isShown(c)} disabled={c.lockedVisible} onChange={() => toggle(c)} className="h-3.5 w-3.5 accent-bronze-500" />
                <span className="min-w-0 flex-1 truncate">{c.label}</span>
                {c.lockedVisible && <Lock className="h-3 w-3 text-ink-300" />}
              </label>
            ))}
          </div>
          <div className="my-1 border-t border-ink-100" />
          <button type="button" onClick={reset} disabled={!changed} className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12.5px] text-ink-800 transition hover:bg-ink-50 disabled:cursor-not-allowed disabled:text-ink-300">
            <RotateCcw className="h-3.5 w-3.5 text-ink-400" />Reset to default
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
}

/** Compare two cell values: numbers and dates as such, text case-insensitively; empties always last. */
function compareValues(a: unknown, b: unknown): number {
  const empty = (v: unknown) => v === null || v === undefined || v === '';
  if (empty(a) && empty(b)) return 0;
  if (empty(a)) return 1;
  if (empty(b)) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  const na = Number(a), nb = Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb) && String(a).trim() !== '' && String(b).trim() !== '') return na - nb;
  return String(a).localeCompare(String(b), 'en-GB', { numeric: true, sensitivity: 'base' });
}

interface Props<T> {
  columns: Column<T>[];
  rows: T[];
  meta?: ListMeta;
  page: number;
  onPage: (p: number) => void;
  isLoading?: boolean;
  isFetching?: boolean;
  onRowClick?: (row: T) => void;
  rowKey: (row: T) => string;
  search?: string;
  onSearch?: (s: string) => void;
  searchPlaceholder?: string;
  filters?: ReactNode;
  /** A row of view pills rendered inside the card, under the search/filter toolbar. */
  tabs?: ReactNode;
  emptyTitle?: string;
  emptyBody?: string;
  /**
   * Server-side sort (useList's `sort`/`setSort`): "-field" = descending. Columns with a `sortKey` sort on the server;
   * the rest sort the loaded page in the browser. Without `onSort`, every heading sorts the loaded page.
   */
  sort?: string;
  onSort?: (sort: string | undefined) => void;
  /** "Export CSV" in the toolbar (shown with `<page>.export`): pass the list's path, page key and current filters. */
  exportCsv?: CsvExport;
  /** A "Columns" button in the toolbar; the user's choice is kept in localStorage under `storageKey`. */
  columnPicker?: { storageKey: string };
  /** Draw the rows as cards instead of a table (the toolbar, empty state and paging stay). `isShown` is the Columns choice, so a card can show the same fields. */
  renderGrid?: (rows: T[], isShown: (key: string) => boolean) => ReactNode;
  /** Extra classes and style for one row (a greyed-out row, an agent's colour stripe, a row that cannot be opened). */
  rowClassName?: (row: T) => string;
  rowStyle?: (row: T) => CSSProperties | undefined;
}

/**
 * The one table. Every list page uses it, so pagination, loading, empty states and
 * mobile behaviour are decided once. Wide content scrolls inside the table, never the page.
 */
export function DataTable<T>({ columns, rows, meta, page, onPage, isLoading, isFetching, onRowClick, rowKey, search, onSearch, searchPlaceholder = 'Search…', filters, tabs, emptyTitle = 'Nothing here yet', emptyBody, sort, onSort, exportCsv, columnPicker, renderGrid, rowClassName, rowStyle }: Props<T>) {
  const picker = useColumnVisibility(columns, columnPicker?.storageKey);
  // A list on the first screen keeps the boot loader up until its rows arrive.
  useHoldWhileLoading(isLoading);
  const shown = picker.visible;
  // Client-side sort of the rows on this page (prototype §38 "any table can be sorted by clicking a column heading").
  const [local, setLocal] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);
  const canExport = useAuth((st) => !!exportCsv && st.can(`${exportCsv.page}.export`));
  const serverField = sort ? sort.replace(/^-/, '') : null;
  const sorted = useMemo(() => {
    if (!local) return rows;
    const c = columns.find((x) => x.key === local.key);
    if (!c) return rows;
    const val = (r: T) => (c.sortValue ? c.sortValue(r) : (r as Record<string, unknown>)[c.key]);
    const out = [...rows].sort((a, b) => compareValues(val(a), val(b)));
    return local.dir === 'desc' ? out.reverse() : out;
  }, [rows, columns, local]);

  const clickHeading = (c: Column<T>) => {
    if (c.sortable === false) return;
    if (onSort && c.sortKey) {
      setLocal(null);
      const field = c.sortKey;
      onSort(serverField !== field ? field : sort?.startsWith('-') ? undefined : `-${field}`);
      return;
    }
    setLocal((cur) => (!cur || cur.key !== c.key ? { key: c.key, dir: 'asc' } : cur.dir === 'asc' ? { key: c.key, dir: 'desc' } : null));
  };
  const dirOf = (c: Column<T>): 'asc' | 'desc' | null => {
    if (onSort && c.sortKey && serverField === c.sortKey) return sort!.startsWith('-') ? 'desc' : 'asc';
    return local?.key === c.key ? local.dir : null;
  };

  return (
    <div className="surface overflow-hidden">
      {(onSearch || filters || canExport || columnPicker) && (
        <ListToolbar search={search} onSearch={onSearch} searchPlaceholder={searchPlaceholder} fetching={isFetching && !isLoading}
          exportCsv={canExport && exportCsv ? { ...exportCsv, query: { ...(exportCsv.query ?? {}), search: search || undefined, sort: sort || undefined } } : undefined}
          filters={<>
            {/* With a Filter card, the column choice is a group inside it; on its own it is a Columns button. */}
            {columnPicker && !filters && <ColumnPicker columns={columns} isShown={picker.isShown} toggle={picker.toggle} reset={picker.reset} changed={picker.changed} />}
            {/* The Filter button is the right-most control on every list. */}
            {columnPicker && filters ? (
              <TableColumnsContext.Provider value={{
                group: { key: '__columns', label: 'Columns', uncounted: true, options: columns.filter((c) => c.label && !c.lockedVisible).map((c) => ({ value: c.key, label: c.label })) },
                value: columns.filter((c) => c.label && !c.lockedVisible && picker.isShown(c)).map((c) => c.key),
                apply: picker.applyShown, reset: picker.reset,
              }}>{filters}</TableColumnsContext.Provider>
            ) : filters}
          </>} />
      )}
      {tabs && <div className="flex flex-wrap items-center gap-1.5 border-b border-ink-200 px-3 py-2">{tabs}</div>}

      {renderGrid ? (
        <div className="p-4">
          {isLoading ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{Array.from({ length: 8 }, (_, i) => <div key={i} className="h-36 animate-pulse rounded-xl bg-ink-100" />)}</div>
          ) : rows.length === 0 ? (
            <div className="px-4 py-12 text-center"><p className="text-[13px] font-medium text-ink-900">{emptyTitle}</p>{emptyBody && <p className="mt-1 text-[12px] text-ink-500">{emptyBody}</p>}</div>
          ) : renderGrid(sorted, (key) => { const c = columns.find((x) => x.key === key); return c ? picker.isShown(c) : true; })}
        </div>
      ) : (
      <div className="overflow-x-auto">
        <table className="w-full min-w-max text-[12.5px]">
          <thead>
            <tr className="bg-ink-50/60 text-left">
              {shown.map((c) => (
                <th key={c.key} className={`whitespace-nowrap px-3 py-1.5 text-[11.5px] font-medium uppercase tracking-wide text-ink-500 ${c.align === 'right' ? 'text-right' : ''} ${c.hideOnMobile ? 'hidden md:table-cell' : ''}`} style={c.width ? { width: c.width } : undefined}>
                  {c.sortable === false || !c.label ? c.label : (
                    <button type="button" onClick={() => clickHeading(c)} className={`inline-flex items-center gap-1 uppercase tracking-wide transition hover:text-ink-900 ${dirOf(c) ? 'text-ink-900' : ''}`} title="Sort">
                      {c.label}
                      {dirOf(c) === 'asc' ? <ArrowUp className="h-3 w-3" /> : dirOf(c) === 'desc' ? <ArrowDown className="h-3 w-3" /> : null}
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>{shown.map((c) => <td key={c.key} className="border-t border-ink-100 px-3 py-2.5"><div className="h-3 w-3/4 animate-pulse rounded bg-ink-100" /></td>)}</tr>
              ))
            ) : rows.length === 0 ? (
              <tr><td colSpan={shown.length} className="border-t border-ink-100 px-4 py-12 text-center"><p className="text-[13px] font-medium text-ink-900">{emptyTitle}</p>{emptyBody && <p className="mt-1 text-[12px] text-ink-500">{emptyBody}</p>}</td></tr>
            ) : sorted.map((row) => (
              <tr key={rowKey(row)} onClick={onRowClick ? () => onRowClick(row) : undefined} className={`transition hover:bg-ink-50/60 ${onRowClick ? 'cursor-pointer' : ''} ${rowClassName?.(row) ?? ''}`} style={rowStyle?.(row)}>
                {shown.map((c) => (
                  <td key={c.key} className={`border-t border-ink-100 px-3 py-1.5 text-[12.5px] text-ink-800 ${c.align === 'right' ? 'text-right tabular-nums' : ''} ${c.hideOnMobile ? 'hidden md:table-cell' : ''}`}>
                    {c.render ? c.render(row) : String((row as Record<string, unknown>)[c.key] ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {meta && meta.totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 border-t border-ink-200 bg-ink-50/50 px-3 py-2 text-[11.5px] text-ink-500">
          <span className="mr-auto tabular-nums">{(page - 1) * meta.perPage + 1}–{Math.min(page * meta.perPage, meta.total)} of {meta.total}</span>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous page" className="grid h-7 w-7 place-items-center rounded-md border border-ink-200 bg-white text-ink-600 transition hover:text-ink-900 disabled:opacity-40"><ChevronLeft className="h-3.5 w-3.5" /></button>
            <span className="tabular-nums">Page {page} of {meta.totalPages}</span>
            <button disabled={page >= meta.totalPages} onClick={() => onPage(page + 1)} aria-label="Next page" className="grid h-7 w-7 place-items-center rounded-md border border-ink-200 bg-white text-ink-600 transition hover:text-ink-900 disabled:opacity-40"><ChevronRight className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      )}
    </div>
  );
}
